import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PhoneCall, Users, BarChart3, Shield, Zap, Clock,
  ChevronRight, Star, Check, ArrowRight, Menu, X,
  Wallet, Mail, Brain, Calendar, Send, Building2, ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { href: "#features", label: "Funktionen" },
  { href: "#how-it-works", label: "So funktioniert's" },
  { href: "#pricing", label: "Preise" },
  { href: "#faq", label: "FAQ" },
  { href: "#kontakt", label: "Kontakt" },
];

const FEATURES = [
  {
    icon: Brain,
    title: "KI-gesteuerte Gespräche",
    desc: "Unsere KI führt natürliche Telefonate mit Ihren Kontakten - kein Roboter-Sound, echte Konversation.",
    color: "bg-[#405189]/10 text-[#405189]"
  },
  {
    icon: Users,
    title: "Kontaktverwaltung",
    desc: "Importieren Sie Kontakte per CSV oder manuell. Verfolgen Sie den Status jedes Leads in Echtzeit.",
    color: "bg-[#0ab39c]/10 text-[#0ab39c]"
  },
  {
    icon: Wallet,
    title: "Transparente Kosten",
    desc: "Nur 0,10 € pro angefangene Minute. Volle Kostenkontrolle mit Echtzeit-Guthaben-Tracking.",
    color: "bg-[#f7b84b]/10 text-[#f7b84b]"
  },
  {
    icon: Mail,
    title: "Automatischer E-Mail-Versand",
    desc: "Bei Interesse wird automatisch eine Info-E-Mail an den Kontakt versendet - ohne Ihr Zutun.",
    color: "bg-[#f06548]/10 text-[#f06548]"
  },
  {
    icon: Calendar,
    title: "Terminplanung",
    desc: "Termine werden automatisch erkannt und in Ihrem Dashboard sowie Google Calendar gespeichert.",
    color: "bg-[#6559cc]/10 text-[#6559cc]"
  },
  {
    icon: BarChart3,
    title: "Echtzeit-Statistiken",
    desc: "Dashboard mit Live-KPIs: Erfolgsrate, Kosten, Anrufhistorie und Kampagnen-Performance.",
    color: "bg-[#299cdb]/10 text-[#299cdb]"
  },
];

const STEPS = [
  { num: "01", title: "Kontakte hochladen", desc: "Laden Sie Ihre Kontaktliste als CSV hoch oder erfassen Sie Kontakte manuell." },
  { num: "02", title: "Kampagne erstellen", desc: "Definieren Sie Ihr Skript, die E-Mail-Vorlage und wählen Sie die KI-Stimme." },
  { num: "03", title: "KI anrufen lassen", desc: "Starten Sie die Anrufe - die KI führt natürliche Gespräche mit Ihren Kontakten." },
  { num: "04", title: "Ergebnisse auswerten", desc: "Sehen Sie Erfolgsraten, Termine und Kosten in Ihrem Dashboard in Echtzeit." },
];

const PRICING = [
  {
    name: "Starter",
    price: "0",
    period: "/ Monat",
    desc: "Perfekt zum Testen",
    features: ["50 € Startguthaben", "Unbegrenzte Kontakte", "1 Kampagne", "KI-Anrufe", "E-Mail Dashboard"],
    cta: "Jetzt starten",
    highlighted: false
  },
  {
    name: "Professional",
    price: "49",
    period: "/ Monat",
    desc: "Für wachsende Teams",
    features: ["100 € Guthaben inkl.", "Unbegrenzte Kontakte", "Unbegrenzte Kampagnen", "Google Calendar Sync", "Prioritäts-Support", "Erweiterte Statistiken"],
    cta: "Jetzt upgraden",
    highlighted: true
  },
  {
    name: "Enterprise",
    price: "Individuell",
    period: "",
    desc: "Für große Organisationen",
    features: ["Individuelles Guthaben", "Eigene KI-Stimme", "API-Zugang", "Dedizierter Account Manager", "SLA-Garantie", "Custom Integrationen"],
    cta: "Kontakt aufnehmen",
    highlighted: false
  },
];

const FAQS = [
  { q: "Wie natürlich klingt die KI?", a: "Unsere Premium KI nutzt die fortschrittlichste Sprachtechnologie auf dem Markt. Die Stimme klingt natürlich und führt echte Dialoge, keine vorgelesenen Skripte." },
  { q: "Was kostet ein Anruf?", a: "Sie zahlen nur 0,10 € pro angefangene Minute. Ein 30-Sekunden-Anruf kostet also 0,10 €, ein 90-Sekunden-Anruf 0,20 €. Keine versteckten Gebühren." },
  { q: "Kann ich meine eigene Telefonnummer verwenden?", a: "Ja, über die Twilio-Integration können Sie Ihre eigene geschäftliche Telefonnummer einrichten. Ihre Kontakte sehen Ihre Nummer als Absender." },
  { q: "Werden Termine automatisch erstellt?", a: "Ja! Wenn die KI im Gespräch einen Termin vereinbart, wird dieser automatisch in Ihrem Dashboard und optional in Google Calendar gespeichert." },
  { q: "Ist das DSGVO-konform?", a: "Ja. Alle Daten werden auf europäischen Servern gespeichert. Sie behalten volle Kontrolle über Ihre Kontaktdaten und können diese jederzeit löschen." },
  { q: "Kann ich die KI-Stimme anpassen?", a: "Im Professional-Plan können Sie aus verschiedenen Stimmen wählen. Im Enterprise-Plan erstellen wir eine individuelle KI-Stimme für Ihr Unternehmen." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", company: "", phone: "", plan: "", message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL + "/api";

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await fetch(`${API}/contact-form`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm),
      });
      setContactSent(true);
      setContactForm({ name: "", email: "", company: "", phone: "", plan: "", message: "" });
    } catch {
      // silent
    } finally {
      setContactLoading(false);
    }
  };

  const scrollToContact = (plan = "") => {
    setContactForm(prev => ({ ...prev, plan }));
    document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white text-[#495057]">
      {/* ===== NAVBAR ===== */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center shrink-0">
              <img src="/logo.png" alt="quofon.de" className="h-10 object-contain" />
            </a>
            <div className="hidden md:flex items-center gap-8">
              {NAV_LINKS.map(l => (
                <a key={l.href} href={l.href} className="text-sm font-medium text-[#495057] hover:text-[#405189] transition-colors">
                  {l.label}
                </a>
              ))}
            </div>
            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate("/login")} className="text-sm font-medium text-[#495057]">
                Anmelden
              </Button>
              <Button onClick={() => navigate("/login")} className="bg-[#405189] hover:bg-[#354270] text-white text-sm px-5">
                Jetzt starten
              </Button>
            </div>
            <button className="md:hidden p-2" onClick={() => setMobileMenu(!mobileMenu)}>
              {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-3">
            {NAV_LINKS.map(l => (
              <a key={l.href} href={l.href} onClick={() => setMobileMenu(false)} className="block text-sm font-medium text-[#495057] py-2">
                {l.label}
              </a>
            ))}
            <Button onClick={() => navigate("/login")} className="w-full bg-[#405189] hover:bg-[#354270] text-white mt-2">
              Jetzt starten
            </Button>
          </div>
        )}
      </nav>

      {/* ===== HERO ===== */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#405189]/5 border border-[#405189]/10 rounded-full text-xs font-semibold text-[#405189] mb-6">
              <Zap className="w-3.5 h-3.5" />
              KI-gestütztes Telefon-Outreach
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2a2f34] leading-[1.1] tracking-tight">
              Automatisierte Anrufe
              <br />
              <span className="text-[#405189]">die sich echt anfühlen</span>
            </h1>
            <p className="mt-6 text-lg text-[#6c757d] max-w-xl mx-auto leading-relaxed">
              quofon.de ruft Ihre Kontakte an, stellt Ihr Projekt vor und vereinbart Termine - vollautomatisch mit KI, die sich wie ein echter Mensch anhört.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => navigate("/login")}
                className="bg-[#405189] hover:bg-[#354270] text-white text-base px-8 py-6 rounded-lg shadow-lg shadow-[#405189]/20 gap-2"
              >
                Jetzt starten
                <ArrowRight className="w-4 h-4" />
              </Button>
              <a href="#how-it-works" className="text-sm font-medium text-[#495057] hover:text-[#405189] flex items-center gap-1.5">
                So funktioniert's
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
            <p className="mt-4 text-xs text-[#919aa3]">50 € Startguthaben inklusive - Keine Kreditkarte erforderlich</p>
          </div>

          {/* Hero visual */}
          <div className="mt-16 max-w-5xl mx-auto">
            <div className="bg-gradient-to-b from-[#f8f9fa] to-white rounded-2xl border border-slate-200/60 shadow-xl p-3">
              <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-slate-100 bg-[#fafbfc]">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  <span className="ml-3 text-[10px] text-slate-400">quofon.de — Dashboard</span>
                </div>
                <div className="grid grid-cols-4 gap-3 p-4">
                  {[
                    { label: "Gesamtanrufe", val: "1.247", change: "+16%", color: "text-[#0ab39c]" },
                    { label: "Erfolgsrate", val: "34.8%", change: "+5%", color: "text-[#0ab39c]" },
                    { label: "Guthaben", val: "847,20 €", change: "", color: "" },
                    { label: "Termine", val: "89", change: "+12", color: "text-[#0ab39c]" },
                  ].map((kpi, i) => (
                    <div key={i} className="bg-[#f8f9fa] rounded-lg p-3">
                      <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold">{kpi.label}</p>
                      <p className="text-lg font-bold text-[#2a2f34] mt-1">{kpi.val}</p>
                      {kpi.change && <p className={`text-[10px] font-semibold ${kpi.color} mt-0.5`}>{kpi.change}</p>}
                    </div>
                  ))}
                </div>
                <div className="px-4 pb-4">
                  <div className="h-32 bg-gradient-to-r from-[#405189]/5 via-[#0ab39c]/5 to-[#405189]/5 rounded-lg flex items-end justify-center gap-1 px-4 pb-3">
                    {[35,52,45,68,42,78,55,90,65,82,58,95].map((h, i) => (
                      <div key={i} className="flex-1 bg-[#405189] rounded-t opacity-70" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <section className="py-8 border-y border-slate-100 bg-[#fafbfc]">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-8 text-sm text-[#919aa3]">
          {[
            { icon: Shield, text: "DSGVO-konform" },
            { icon: Zap, text: "Premium KI" },
            { icon: PhoneCall, text: "Twilio Carrier" },
            { icon: Clock, text: "Echtzeit-Tracking" },
            { icon: Star, text: "4.9/5 Bewertung" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 font-medium">
              <item.icon className="w-4 h-4 text-[#405189]" />
              {item.text}
            </div>
          ))}
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="py-20 lg:py-28 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-sm font-semibold text-[#0ab39c] uppercase tracking-wider mb-3">Funktionen</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2a2f34] tracking-tight">
              Alles was Sie für erfolgreiches Outreach brauchen
            </h2>
            <p className="mt-4 text-[#6c757d]">
              Von der Kontaktverwaltung bis zum automatischen Termin - quofon.de deckt den gesamten Workflow ab.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="group p-6 rounded-xl border border-slate-100 hover:border-[#405189]/20 hover:shadow-lg hover:shadow-[#405189]/5 transition-all duration-300">
                <div className={`w-12 h-12 rounded-lg ${f.color} flex items-center justify-center mb-4`}>
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-[#2a2f34] mb-2">{f.title}</h3>
                <p className="text-sm text-[#6c757d] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-20 lg:py-28 px-4 bg-[#fafbfc]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-sm font-semibold text-[#0ab39c] uppercase tracking-wider mb-3">So funktioniert's</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2a2f34] tracking-tight">
              In 4 Schritten zum Erfolg
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <div key={i} className="relative">
                <div className="text-5xl font-black text-[#405189]/10 mb-3">{s.num}</div>
                <h3 className="text-lg font-semibold text-[#2a2f34] mb-2">{s.title}</h3>
                <p className="text-sm text-[#6c757d] leading-relaxed">{s.desc}</p>
                {i < 3 && (
                  <div className="hidden lg:block absolute top-8 -right-4 text-[#405189]/20">
                    <ChevronRight className="w-8 h-8" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-20 lg:py-28 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-sm font-semibold text-[#0ab39c] uppercase tracking-wider mb-3">Preise</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2a2f34] tracking-tight">
              Transparent und fair
            </h2>
            <p className="mt-4 text-[#6c757d]">Faire Preise für jede Unternehmensgröße.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PRICING.map((plan, i) => (
              <div
                key={i}
                className={`relative rounded-2xl p-6 ${
                  plan.highlighted
                    ? "bg-[#405189] text-white border-2 border-[#405189] shadow-xl shadow-[#405189]/20 scale-[1.03]"
                    : "bg-white border border-slate-200"
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0ab39c] text-white text-xs font-bold px-4 py-1 rounded-full">
                    Beliebt
                  </div>
                )}
                <h3 className={`text-lg font-semibold ${plan.highlighted ? "text-white" : "text-[#2a2f34]"}`}>{plan.name}</h3>
                <p className={`text-sm mt-1 ${plan.highlighted ? "text-white/70" : "text-[#919aa3]"}`}>{plan.desc}</p>
                <div className="mt-6 mb-6">
                  <span className={`text-4xl font-bold ${plan.highlighted ? "text-white" : "text-[#2a2f34]"}`}>
                    {plan.price === "Individuell" ? "" : ""}{plan.price}{plan.price !== "Individuell" ? " €" : ""}
                  </span>
                  <span className={`text-sm ${plan.highlighted ? "text-white/60" : "text-[#919aa3]"}`}>{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlighted ? "text-[#0ab39c]" : "text-[#0ab39c]"}`} />
                      <span className={plan.highlighted ? "text-white/90" : "text-[#6c757d]"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => plan.name === "Enterprise" ? scrollToContact(plan.name) : navigate("/login")}
                  className={`w-full ${
                    plan.highlighted
                      ? "bg-white text-[#405189] hover:bg-white/90"
                      : "bg-[#405189] text-white hover:bg-[#354270]"
                  }`}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-center mt-8 text-sm text-[#919aa3]">
            Zusätzlich: 0,10 € pro angefangene Gesprächsminute. Guthaben jederzeit aufladbar.
          </p>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="py-20 lg:py-28 px-4 bg-[#fafbfc]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#0ab39c] uppercase tracking-wider mb-3">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2a2f34] tracking-tight">
              Häufig gestellte Fragen
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-slate-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                >
                  <span className="font-semibold text-[#2a2f34] text-sm pr-4">{faq.q}</span>
                  <ChevronRight className={`w-4 h-4 text-[#919aa3] shrink-0 transition-transform duration-200 ${openFaq === i ? "rotate-90" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-sm text-[#6c757d] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT FORM ===== */}
      <section id="kontakt" className="py-20 lg:py-28 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <p className="text-sm font-semibold text-[#0ab39c] uppercase tracking-wider mb-3">Kontakt</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2a2f34] tracking-tight">
                Lassen Sie uns sprechen
              </h2>
              <p className="mt-4 text-[#6c757d] leading-relaxed">
                Haben Sie Fragen zu unseren Tarifen oder benötigen eine individuelle Lösung?
                Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden bei Ihnen.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#405189]/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#405189]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2a2f34]">E-Mail</p>
                    <p className="text-sm text-[#6c757d]">info@quofon.de</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#405189]/10 flex items-center justify-center">
                    <PhoneCall className="w-5 h-5 text-[#405189]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2a2f34]">Support</p>
                    <p className="text-sm text-[#6c757d]">Mo-Fr 9:00 - 18:00 Uhr</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#405189]/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#405189]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2a2f34]">Unternehmen</p>
                    <p className="text-sm text-[#6c757d]">quofon.de - Deutschland</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              {contactSent ? (
                <div className="bg-[#0ab39c]/10 border border-[#0ab39c]/20 rounded-2xl p-8 text-center" data-testid="contact-success">
                  <Check className="w-12 h-12 text-[#0ab39c] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[#2a2f34] mb-2">Nachricht gesendet!</h3>
                  <p className="text-[#6c757d]">Wir melden uns schnellstmöglich bei Ihnen.</p>
                  <Button onClick={() => setContactSent(false)} variant="ghost" className="mt-4 text-[#405189]">
                    Weitere Nachricht senden
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#495057] mb-1.5 block">Name *</label>
                      <input
                        required value={contactForm.name}
                        onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#405189]/20 focus:border-[#405189]"
                        placeholder="Max Mustermann"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#495057] mb-1.5 block">E-Mail *</label>
                      <input
                        type="email" required value={contactForm.email}
                        onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#405189]/20 focus:border-[#405189]"
                        placeholder="name@firma.de"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#495057] mb-1.5 block">Unternehmen</label>
                      <input
                        value={contactForm.company}
                        onChange={e => setContactForm(p => ({ ...p, company: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#405189]/20 focus:border-[#405189]"
                        placeholder="Firmenname"
                        data-testid="contact-company"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#495057] mb-1.5 block">Telefon</label>
                      <input
                        value={contactForm.phone}
                        onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#405189]/20 focus:border-[#405189]"
                        placeholder="+49 123 456789"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#495057] mb-1.5 block">Gewünschter Tarif</label>
                    <select
                      value={contactForm.plan}
                      onChange={e => setContactForm(p => ({ ...p, plan: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#405189]/20 focus:border-[#405189] bg-white"
                      data-testid="contact-plan"
                    >
                      <option value="">Bitte wählen...</option>
                      <option value="Starter">Starter (0 €/Monat)</option>
                      <option value="Professional">Professional (49 €/Monat)</option>
                      <option value="Enterprise">Enterprise (Individuell)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#495057] mb-1.5 block">Nachricht *</label>
                    <textarea
                      required rows={4} value={contactForm.message}
                      onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#405189]/20 focus:border-[#405189] resize-none"
                      placeholder="Ihre Nachricht..."
                      data-testid="contact-message"
                    />
                  </div>
                  <Button
                    type="submit" disabled={contactLoading}
                    className="w-full bg-[#405189] hover:bg-[#354270] text-white py-5 gap-2"
                    data-testid="contact-submit"
                  >
                    {contactLoading ? "Wird gesendet..." : <><Send className="w-4 h-4" /> Nachricht senden</>}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20 lg:py-28 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-[#405189] rounded-2xl p-10 lg:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                Starten Sie heute mit quofon.de
              </h2>
              <p className="mt-4 text-white/70 text-lg max-w-lg mx-auto">
                50 € Startguthaben inklusive. Keine Kreditkarte. Keine Verpflichtung.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="mt-8 bg-white text-[#405189] hover:bg-white/90 text-base px-8 py-6 rounded-lg shadow-lg gap-2"
              >
                Jetzt starten
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#2a2f34] text-white/70 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="md:col-span-1">
              <img src="/logo-white.png" alt="quofon.de" className="h-10 object-contain mb-4" />
              <p className="text-sm leading-relaxed">
                KI-gesteuerte Telefonanrufe die sich echt anfühlen. Automatisiertes Outreach für moderne Unternehmen.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Produkt</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Funktionen</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Preise</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">So funktioniert's</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Rechtliches</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Impressum</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Datenschutz</a></li>
                <li><a href="#" className="hover:text-white transition-colors">AGB</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cookies</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Kontakt</h4>
              <ul className="space-y-2.5 text-sm">
                <li>info@quofon.de</li>
                <li>Support Mo-Fr 9-18 Uhr</li>
                <li className="pt-2">
                  <Button
                    onClick={() => navigate("/login")}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 text-xs"
                  >
                    Zum Login
                  </Button>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs">© {new Date().getFullYear()} quofon.de. Alle Rechte vorbehalten.</p>
            <p className="text-xs">Made in Germany</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
